'use strict';

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

/* =========================================================================
   Utilities
   ========================================================================= */

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

function escapeHtml(str) {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return String(str).replace(/[&<>"']/g, c => map[c]);
}

function trapFocus(container) {
  const focusable = $$('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])', container);
  if (!focusable.length) return null;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  function handler(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  container.addEventListener('keydown', handler);
  first.focus();
  return () => container.removeEventListener('keydown', handler);
}

function debounce(fn, ms) {
  let id;
  return (...args) => { clearTimeout(id); id = setTimeout(() => fn(...args), ms); };
}

/* =========================================================================
   1. Header Scroll
   ========================================================================= */

(function headerScroll() {
  const header = $('.site-header');
  if (!header) return;
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        header.classList.toggle('scrolled', window.scrollY > 50);
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
})();

/* =========================================================================
   2. Mobile Menu
   ========================================================================= */

(function mobileMenu() {
  const menu = $('#mobile-menu');
  const overlay = $('#mobile-menu-overlay');
  const openBtn = $('[data-mobile-menu-open]');
  const closeBtn = $('[data-mobile-menu-close]');
  if (!menu) return;

  let releaseFocus = null;

  function open() {
    menu.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    releaseFocus = trapFocus(menu);
  }

  function close() {
    menu.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (releaseFocus) { releaseFocus(); releaseFocus = null; }
    if (openBtn) openBtn.focus();
  }

  if (openBtn) openBtn.addEventListener('click', open);
  if (closeBtn) closeBtn.addEventListener('click', close);
  if (overlay) overlay.addEventListener('click', close);

  menu.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });
})();

/* =========================================================================
   3. Cart Drawer
   ========================================================================= */

(function cartDrawer() {
  const drawer = $('#cart-drawer');
  const overlay = $('#cart-overlay');
  const openBtns = $$('[data-cart-open]');
  const closeBtn = $('[data-cart-close]');
  if (!drawer) return;

  let releaseFocus = null;

  function open() {
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    releaseFocus = trapFocus(drawer);
    fetchCart();
  }

  function close() {
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    if (releaseFocus) { releaseFocus(); releaseFocus = null; }
  }

  openBtns.forEach(btn => btn.addEventListener('click', open));
  if (closeBtn) closeBtn.addEventListener('click', close);
  if (overlay) overlay.addEventListener('click', close);

  drawer.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });

  window.openCartDrawer = open;
  window.closeCartDrawer = close;
})();

/* =========================================================================
   4. Fetch Cart
   ========================================================================= */

async function fetchCart() {
  try {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    const cart = await res.json();
    updateCartDrawer(cart);
    return cart;
  } catch (err) {
    console.error('[theme] fetchCart error:', err);
  }
}

window.fetchCart = fetchCart;

/* =========================================================================
   5. Update Cart Drawer
   ========================================================================= */

function updateCartDrawer(cart) {
  const container = $('#cart-drawer-items');
  const badges = $$('.cart-count');

  badges.forEach(badge => { badge.textContent = cart.item_count; });

  if (!container) return;

  if (!cart.items.length) {
    container.innerHTML = `
      <div class="cart-empty">
        <p>Your cart is empty</p>
        <a href="/collections/all" class="btn btn--primary">Continue Shopping</a>
      </div>`;
    return;
  }

  container.innerHTML = cart.items.map(item => `
    <div class="cart-item" data-key="${escapeHtml(item.key)}">
      <div class="cart-item__image">
        <img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" width="80" height="80" loading="lazy">
      </div>
      <div class="cart-item__details">
        <h4 class="cart-item__title">${escapeHtml(item.product_title)}</h4>
        ${item.variant_title ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
        <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
        <div class="cart-item__qty">
          <button type="button" class="cart-item__qty-btn" data-cart-qty-btn="minus" data-key="${escapeHtml(item.key)}" aria-label="Decrease quantity">−</button>
          <span class="cart-item__qty-value">${item.quantity}</span>
          <button type="button" class="cart-item__qty-btn" data-cart-qty-btn="plus" data-key="${escapeHtml(item.key)}" aria-label="Increase quantity">+</button>
        </div>
      </div>
    </div>`).join('');

  const subtotal = $('#cart-drawer-subtotal');
  if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

  container.addEventListener('click', e => {
    const btn = e.target.closest('[data-cart-qty-btn]');
    if (!btn) return;
    const key = btn.dataset.key;
    const item = cart.items.find(i => i.key === key);
    if (!item) return;
    const newQty = btn.dataset.cartQtyBtn === 'plus' ? item.quantity + 1 : item.quantity - 1;
    updateCartItem(key, newQty);
  });
}

/* =========================================================================
   6. Update Cart Item
   ========================================================================= */

async function updateCartItem(key, qty) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
    });
    const cart = await res.json();
    updateCartDrawer(cart);
  } catch (err) {
    console.error('[theme] updateCartItem error:', err);
  }
}

/* =========================================================================
   7. Add to Cart
   ========================================================================= */

async function addToCart(variantId, qty = 1) {
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    if (!res.ok) throw new Error(`Add to cart failed: ${res.status}`);
    await fetchCart();
    window.openCartDrawer();
  } catch (err) {
    console.error('[theme] addToCart error:', err);
  }
}

/* =========================================================================
   10. Add to Cart Forms
   ========================================================================= */

(function addToCartForms() {
  document.addEventListener('submit', async e => {
    const form = e.target.closest('[data-add-to-cart-form]');
    if (!form) return;
    e.preventDefault();

    const btn = $('[data-add-to-cart-btn]', form) || $('button[type="submit"]', form);
    const originalText = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    const variantInput = $('input[name="id"]', form);
    const qtyInput = $('input[name="quantity"]', form);
    const variantId = variantInput ? variantInput.value : form.dataset.variantId;
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

    await addToCart(Number(variantId), qty);

    if (btn) {
      btn.textContent = 'Added!';
      setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 1500);
    }
  });
})();

/* =========================================================================
   11. Quick Add
   ========================================================================= */

(function quickAdd() {
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();

    const variantId = btn.dataset.quickAdd;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    await addToCart(Number(variantId));

    btn.textContent = 'Added!';
    setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 1500);
  });
})();

/* =========================================================================
   12. Product Gallery
   ========================================================================= */

(function productGallery() {
  const mainImg = $('#gallery-main-img');
  if (!mainImg) return;

  document.addEventListener('click', e => {
    const thumb = e.target.closest('.gallery-thumb');
    if (!thumb) return;
    const src = thumb.dataset.src || thumb.src;
    const alt = thumb.dataset.alt || thumb.alt || '';
    mainImg.src = src;
    mainImg.alt = alt;
    $$('.gallery-thumb').forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
  });
})();

/* =========================================================================
   13. Quantity Controls
   ========================================================================= */

(function quantityControls() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-qty-btn]');
    if (!btn) return;
    const wrapper = btn.closest('[data-qty-wrapper]') || btn.parentElement;
    const input = $('input[type="number"]', wrapper);
    if (!input) return;

    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 9999;
    let val = parseInt(input.value, 10) || min;

    if (btn.dataset.qtyBtn === 'increase') val = Math.min(val + 1, max);
    else val = Math.max(val - 1, min);

    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();

/* =========================================================================
   14. Scroll Reveal
   ========================================================================= */

(function scrollReveal() {
  if (!document.body.classList.contains('animations-enabled')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  $$('.reveal').forEach(el => observer.observe(el));
})();

/* =========================================================================
   15. Variant Selector
   ========================================================================= */

(function variantSelector() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-variant-btn]');
    if (!btn) return;
    const group = btn.closest('[data-variant-group]') || btn.parentElement;
    $$('[data-variant-btn]', group).forEach(b => b.classList.remove('selected'));
    btn.classList.add('selected');

    const form = btn.closest('form') || btn.closest('[data-add-to-cart-form]');
    if (form) {
      const hidden = $('input[name="id"]', form);
      if (hidden) hidden.value = btn.dataset.variantBtn;
    }
  });
})();

/* =========================================================================
   16. Newsletter Form
   ========================================================================= */

(function newsletterForm() {
  document.addEventListener('submit', async e => {
    const form = e.target.closest('[data-newsletter-form]');
    if (!form) return;
    e.preventDefault();

    const btn = $('button[type="submit"]', form);
    const originalText = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Subscribing…'; }

    try {
      const formData = new FormData(form);
      await fetch(form.action || '/contact#newsletter', {
        method: 'POST',
        body: formData
      });
      if (btn) btn.textContent = 'Subscribed!';
      form.reset();
    } catch (err) {
      console.error('[theme] newsletter error:', err);
      if (btn) { btn.textContent = 'Error – try again'; }
    } finally {
      setTimeout(() => { if (btn) { btn.textContent = originalText; btn.disabled = false; } }, 3000);
    }
  });
})();

/* =========================================================================
   17. Announcement Bar Dismiss
   ========================================================================= */

(function announcementDismiss() {
  const dismissBtn = $('#announcement-dismiss');
  if (!dismissBtn) return;

  dismissBtn.addEventListener('click', () => {
    const bar = dismissBtn.closest('.announcement-bar');
    if (!bar) return;
    bar.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
    bar.style.opacity = '0';
    bar.style.maxHeight = '0';
    bar.style.overflow = 'hidden';
    setTimeout(() => bar.remove(), 300);
  });
})();

/* =========================================================================
   18. Search Overlay + Predictive Search
   ========================================================================= */

(function searchOverlay() {
  const overlay = $('#search-overlay');
  const openBtn = $('#search-open');
  const closeBtn = $('#search-close');
  if (!overlay) return;

  const input = $('input[type="search"]', overlay) || $('input', overlay);
  const resultsContainer = $('.search-results', overlay);
  let releaseFocus = null;
  let activeIndex = -1;

  function open() {
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    releaseFocus = trapFocus(overlay);
    if (input) input.focus();
  }

  function close() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    if (releaseFocus) { releaseFocus(); releaseFocus = null; }
    if (input) input.value = '';
    if (resultsContainer) resultsContainer.innerHTML = '';
    activeIndex = -1;
  }

  if (openBtn) openBtn.addEventListener('click', open);
  if (closeBtn) closeBtn.addEventListener('click', close);

  overlay.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });

  document.addEventListener('keydown', e => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      overlay.classList.contains('is-open') ? close() : open();
    }
  });

  if (!input || !resultsContainer) return;

  const search = debounce(async (query) => {
    if (query.length < 2) { resultsContainer.innerHTML = ''; return; }
    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article,page&resources[limit]=4`);
      const data = await res.json();
      renderResults(data.resources.results);
    } catch (err) {
      console.error('[theme] predictive search error:', err);
    }
  }, 250);

  input.addEventListener('input', () => { activeIndex = -1; search(input.value.trim()); });

  function renderResults(results) {
    let html = '';
    const { products = [], collections = [], articles = [], pages = [] } = results;

    if (products.length) {
      html += `<div class="search-group"><h4 class="search-group__title">Products</h4>`;
      html += products.map(p => `
        <a href="${escapeHtml(p.url)}" class="search-result-item">
          ${p.image ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" width="40" height="40" loading="lazy">` : ''}
          <span class="search-result-item__title">${escapeHtml(p.title)}</span>
          <span class="search-result-item__price">${formatMoney(p.price)}</span>
        </a>`).join('');
      html += `</div>`;
    }

    if (collections.length) {
      html += `<div class="search-group"><h4 class="search-group__title">Collections</h4>`;
      html += collections.map(c => `<a href="${escapeHtml(c.url)}" class="search-result-item"><span class="search-result-item__title">${escapeHtml(c.title)}</span></a>`).join('');
      html += `</div>`;
    }

    if (articles.length) {
      html += `<div class="search-group"><h4 class="search-group__title">Articles</h4>`;
      html += articles.map(a => `<a href="${escapeHtml(a.url)}" class="search-result-item"><span class="search-result-item__title">${escapeHtml(a.title)}</span></a>`).join('');
      html += `</div>`;
    }

    if (pages.length) {
      html += `<div class="search-group"><h4 class="search-group__title">Pages</h4>`;
      html += pages.map(p => `<a href="${escapeHtml(p.url)}" class="search-result-item"><span class="search-result-item__title">${escapeHtml(p.title)}</span></a>`).join('');
      html += `</div>`;
    }

    if (!html) html = '<p class="search-no-results">No results found</p>';
    resultsContainer.innerHTML = html;
  }

  overlay.addEventListener('keydown', e => {
    const items = $$('.search-result-item', resultsContainer);
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % items.length;
      items.forEach((el, i) => el.classList.toggle('is-highlighted', i === activeIndex));
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = (activeIndex - 1 + items.length) % items.length;
      items.forEach((el, i) => el.classList.toggle('is-highlighted', i === activeIndex));
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      items[activeIndex].click();
    }
  });
})();

/* =========================================================================
   19. Copy URL (Social Share)
   ========================================================================= */

(function copyUrl() {
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-copy-url]');
    if (!btn) return;
    e.preventDefault();
    const url = btn.dataset.copyUrl || window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      const original = btn.innerHTML;
      btn.innerHTML = '✓ Copied!';
      btn.classList.add('copied');
      setTimeout(() => { btn.innerHTML = original; btn.classList.remove('copied'); }, 2000);
    } catch (err) {
      console.error('[theme] copy URL error:', err);
    }
  });
})();

/* =========================================================================
   20. Back to Top
   ========================================================================= */

(function backToTop() {
  const btn = $('#back-to-top');
  if (!btn) return;

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        btn.classList.toggle('is-visible', window.scrollY > 600);
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });

  btn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
})();

/* =========================================================================
   21. Cookie Consent
   ========================================================================= */

(function cookieConsent() {
  const banner = $('#cookie-consent');
  if (!banner) return;
  if (localStorage.getItem('cookie-consent')) return;

  setTimeout(() => { banner.classList.add('is-visible'); }, 2000);

  const acceptBtn = $('[data-cookie-accept]', banner);
  const declineBtn = $('[data-cookie-decline]', banner);

  function dismiss(value) {
    localStorage.setItem('cookie-consent', value);
    banner.classList.remove('is-visible');
    setTimeout(() => banner.remove(), 300);
  }

  if (acceptBtn) acceptBtn.addEventListener('click', () => dismiss('accepted'));
  if (declineBtn) declineBtn.addEventListener('click', () => dismiss('declined'));
})();

/* =========================================================================
   22. Countdown Timer
   ========================================================================= */

(function countdownTimer() {
  const timers = $$('[data-countdown-target]');
  if (!timers.length) return;

  function update(el) {
    const target = new Date(el.dataset.countdownTarget).getTime();
    const now = Date.now();
    const diff = Math.max(0, target - now);

    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);

    const daysEl = $('[data-countdown-days]', el);
    const hoursEl = $('[data-countdown-hours]', el);
    const minsEl = $('[data-countdown-minutes]', el);
    const secsEl = $('[data-countdown-seconds]', el);

    if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
    if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
    if (minsEl) minsEl.textContent = String(mins).padStart(2, '0');
    if (secsEl) secsEl.textContent = String(secs).padStart(2, '0');
  }

  timers.forEach(el => {
    update(el);
    setInterval(() => update(el), 1000);
  });
})();

/* =========================================================================
   23. Product Tabs
   ========================================================================= */

(function productTabs() {
  const tabContainers = $$('[data-product-tabs]');
  tabContainers.forEach(container => {
    const buttons = $$('[data-tab-btn]', container);
    const panels = $$('[data-tab-panel]', container);

    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.tabBtn;

        buttons.forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');

        panels.forEach(panel => {
          panel.classList.toggle('is-active', panel.dataset.tabPanel === target);
        });
      });
    });
  });
})();

/* =========================================================================
   24. Sticky Add to Cart
   ========================================================================= */

(function stickyAtc() {
  const stickyBar = $('#sticky-atc');
  const form = $('[data-add-to-cart-form]');
  if (!stickyBar || !form) return;

  const observer = new IntersectionObserver(([entry]) => {
    stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
  }, { threshold: 0 });

  observer.observe(form);
})();

/* =========================================================================
   25. Recently Viewed
   ========================================================================= */

(function recentlyViewed() {
  const STORAGE_KEY = 'recently-viewed';
  const MAX_ITEMS = 10;
  const grid = $('[data-recently-viewed-grid]');
  const currentHandle = document.body.dataset.productHandle;

  if (currentHandle) {
    let handles = [];
    try { handles = JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch {}
    handles = handles.filter(h => h !== currentHandle);
    handles.unshift(currentHandle);
    handles = handles.slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  }

  if (!grid) return;

  async function render() {
    let handles = [];
    try { handles = JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch {}
    if (currentHandle) handles = handles.filter(h => h !== currentHandle);
    handles = handles.slice(0, 4);
    if (!handles.length) return;

    const products = await Promise.all(
      handles.map(async handle => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) return null;
          return res.json();
        } catch { return null; }
      })
    );

    const valid = products.filter(Boolean);
    if (!valid.length) return;

    grid.innerHTML = valid.map(p => `
      <a href="${escapeHtml(p.url)}" class="product-card product-card--recently-viewed">
        <div class="product-card__image">
          <img src="${escapeHtml(p.featured_image)}" alt="${escapeHtml(p.title)}" width="300" height="300" loading="lazy">
        </div>
        <h4 class="product-card__title">${escapeHtml(p.title)}</h4>
        <p class="product-card__price">${formatMoney(p.price)}</p>
      </a>`).join('');
  }

  render();
})();

/* =========================================================================
   27. Initial Cart Count
   ========================================================================= */

(function initialCartCount() {
  fetchCart();
})();

/* =========================================================================
   Video Reviews Carousel
   Infinite circular carousel with scale-perspective and no-gap positioning.
   ========================================================================= */

(function initVideoReviews() {
  const section = document.querySelector('.video-reviews');
  if (!section) return;

  const stage    = section.querySelector('.vr-stage');
  const prevBtn  = section.querySelector('.vr-arrow--prev');
  const nextBtn  = section.querySelector('.vr-arrow--next');
  const dotsWrap = section.querySelector('.vr-dots');
  if (!stage) return;

  const cards = [...stage.querySelectorAll('.vr-card')];
  const N     = cards.length;
  if (N < 2) return;

  const HALF      = Math.floor(N / 2);
  const SCALES    = [1.0, 0.82, 0.67, 0.55, 0.44];
  const OPACITIES = [1.0, 0.88, 0.65, 0.40, 0.20];
  const GAP_OVERRIDE = parseInt(section.dataset.cardGap, 10);
  const GAP       = Number.isNaN(GAP_OVERRIDE) ? 10 : GAP_OVERRIDE;
  const AUTOPLAY_MS = (parseFloat(section.dataset.autoplaySpeed) || 6) * 1000;
  const TRANS     = 'transform 0.52s cubic-bezier(0.22,1,0.36,1), opacity 0.52s ease';

  let current  = HALF;
  let autoTimer;
  let xPos     = [0];

  /* ── x-positions: cards touch edge-to-edge + GAP ── */
  function buildXPos() {
    const cardW = cards[0].offsetWidth;
    xPos = [0];
    for (let d = 1; d <= HALF; d++) {
      const hw1 = (SCALES[Math.min(d - 1, SCALES.length - 1)] * cardW) / 2;
      const hw2 = (SCALES[Math.min(d,     SCALES.length - 1)] * cardW) / 2;
      xPos[d]   = xPos[d - 1] + hw1 + hw2 + GAP;
    }
  }

  /* ── Circular signed offset of card i from current ── */
  function circOffset(i) {
    return ((i - current + HALF + N) % N) - HALF;
  }

  /* ── Render all cards ── */
  function applyLayout(skipSet) {
    const dots = dotsWrap ? [...dotsWrap.querySelectorAll('.vr-dot')] : [];
    cards.forEach((card, i) => {
      const off  = circOffset(i);
      const dist = Math.abs(off);
      const si   = Math.min(dist, SCALES.length    - 1);
      const oi   = Math.min(dist, OPACITIES.length - 1);
      const tx   = off >= 0 ? xPos[dist] : -xPos[dist];

      card.style.transition = skipSet.has(i) ? 'none' : TRANS;
      card.style.transform  = `translateX(${tx}px) scale(${SCALES[si]})`;
      card.style.opacity    = OPACITIES[oi];
      card.style.zIndex     = 10 - dist;
      card.classList.toggle('is-active', i === current);
    });
    dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
  }

  /* ── Navigate ── */
  function goTo(nextIdx) {
    nextIdx = ((nextIdx % N) + N) % N;

    const wrapSet = new Set();
    cards.forEach((_, i) => {
      const oldOff = circOffset(i);
      const newOff = ((i - nextIdx + HALF + N) % N) - HALF;
      if (Math.abs(newOff - oldOff) > 1) wrapSet.add(i);
    });

    current = nextIdx;

    if (wrapSet.size > 0) {
      applyLayout(wrapSet);
      stage.offsetHeight; /* force reflow */
      wrapSet.forEach(i => (cards[i].style.transition = TRANS));
    } else {
      applyLayout(new Set());
    }

    /* Play video on active card, pause others */
    cards.forEach((card, i) => {
      const video = card.querySelector('video');
      if (!video) return;
      if (i === current) {
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }

  /* ── Dots ── */
  function buildDots() {
    if (!dotsWrap) return;
    dotsWrap.innerHTML = '';
    for (let i = 0; i < N; i++) {
      const d = document.createElement('button');
      d.className = 'vr-dot';
      d.setAttribute('aria-label', `Review ${i + 1}`);
      d.addEventListener('click', () => { goTo(i); resetAuto(); });
      dotsWrap.appendChild(d);
    }
  }

  /* ── Autoplay ── */
  function startAuto() { autoTimer = setInterval(() => goTo(current + 1), AUTOPLAY_MS); }
  function resetAuto()  { clearInterval(autoTimer); startAuto(); }

  /* ── Events ── */
  if (prevBtn) prevBtn.addEventListener('click', () => { goTo(current - 1); resetAuto(); });
  if (nextBtn) nextBtn.addEventListener('click', () => { goTo(current + 1); resetAuto(); });

  stage.addEventListener('click', e => {
    const card = e.target.closest('.vr-card');
    if (!card) return;
    const idx = cards.indexOf(card);
    if (idx !== current) { goTo(idx); resetAuto(); }
  });

  let startX = 0;
  stage.addEventListener('pointerdown', e => { startX = e.clientX; });
  stage.addEventListener('pointerup',   e => {
    const diff = startX - e.clientX;
    if (Math.abs(diff) > 50) { goTo(current + (diff > 0 ? 1 : -1)); resetAuto(); }
  });

  stage.addEventListener('mouseenter', () => clearInterval(autoTimer));
  stage.addEventListener('mouseleave', startAuto);

  /* ── Init ── */
  function init() {
    buildXPos();
    buildDots();
    const allIdx = new Set(cards.map((_, i) => i));
    applyLayout(allIdx);
    requestAnimationFrame(() => cards.forEach(c => (c.style.transition = TRANS)));
    startAuto();
  }

  init();

  window.addEventListener('resize', debounce(() => {
    buildXPos();
    applyLayout(new Set());
  }, 150));
})();

/* =========================================================================
   Product Recommendations (related products section)
   ========================================================================= */

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;

    const load = () => {
      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const source = doc.querySelector('product-recommendations');
          if (source && source.innerHTML.trim()) {
            this.innerHTML = source.innerHTML;
          }
        })
        .catch(() => {});
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    } else {
      load();
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
